// 侧栏菜单定义 + 隐藏偏好（单一真源）
// - Sidebar 与「系统设置 → 侧栏菜单」都消费这里的 NAV_ITEMS，不各自维护一份。
// - 偏好存 localStorage 而不是 D1：这是「本设备的视图偏好」，跟 theme 同级；
//   settings 表是 workspace 级共享配置，把它写进去会连带改掉其他成员的侧栏。
// - locked 项（仪表盘 / 系统设置）不可隐藏：隐藏系统设置后就没有入口改回来了。
// - 隐藏只影响侧栏渲染，路由与 ⌘K 搜索不受影响，直接访问 URL 仍然可用。

import {
  AtSign,
  Clock,
  Filter,
  Inbox,
  Key,
  LayoutDashboard,
  MessageCircle,
  Paperclip,
  Search,
  Send,
  Settings,
  Sparkles,
  Tags,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useCallback, useSyncExternalStore } from 'react';

export type NavGroup = '核心' | '工具' | 'AI' | '系统';

export interface NavItem {
  /** 稳定 key，改路由也不丢用户偏好 */
  key: string;
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  group: NavGroup;
  badge?: string;
  /** true = 不允许隐藏 */
  locked?: boolean;
}

// F67：「转发设置」已并入规则页 tab（/app/rules?tab=forwards），不再是独立菜单项
export const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', to: '/app', label: '仪表盘', icon: LayoutDashboard, group: '核心', locked: true },
  { key: 'aliases', to: '/app/aliases', label: '别名管理', icon: AtSign, group: '核心' },
  { key: 'source-accounts', to: '/app/source-accounts', label: '上游账号', icon: UserRound, group: '核心' },
  { key: 'inbox', to: '/app/inbox', label: '收件箱', icon: Inbox, group: '核心' },
  { key: 'outbound', to: '/app/outbound', label: '外发邮件', icon: Send, group: '核心' },
  { key: 'search', to: '/app/search', label: '邮件搜索', icon: Search, group: '工具' },
  { key: 'rules', to: '/app/rules', label: '规则管理', icon: Filter, group: '工具' },
  { key: 'temporary', to: '/app/temporary', label: '临时别名', icon: Clock, group: '工具' },
  { key: 'attachments', to: '/app/attachments', label: '附件管理', icon: Paperclip, group: '工具' },
  { key: 'trash', to: '/app/trash', label: '回收站', icon: Trash2, group: '工具' },
  { key: 'ai', to: '/app/ai', label: 'AI 助手', icon: Sparkles, group: 'AI', badge: 'Beta' },
  { key: 'ai-labels', to: '/app/ai-labels', label: 'AI 打标签', icon: Tags, group: 'AI', badge: 'Beta' },
  { key: 'ai-qa', to: '/app/ai-qa', label: 'AI 问答', icon: MessageCircle, group: 'AI', badge: 'Beta' },
  { key: 'api-keys', to: '/app/api-keys', label: 'API 管理', icon: Key, group: '系统' },
  { key: 'settings', to: '/app/settings', label: '系统设置', icon: Settings, group: '系统', locked: true },
];

export const NAV_GROUP_ORDER: NavGroup[] = ['核心', '工具', 'AI', '系统'];

const STORAGE_KEY = 'aliashub.nav.hidden';

// ---- 极简外部 store：Sidebar 与设置页共享同一份状态，跨标签页也同步 ----

let cache: string[] = readStorage();
const listeners = new Set<() => void>();

function readStorage(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const lockedKeys = new Set(NAV_ITEMS.filter((i) => i.locked).map((i) => i.key));
    return parsed.filter((k): k is string => typeof k === 'string' && !lockedKeys.has(k));
  } catch {
    return [];
  }
}

function emit(): void {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    cache = readStorage();
    emit();
  });
}

function setHidden(next: string[]): void {
  cache = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // 隐身模式 / 存储被禁用：本次会话内仍生效，只是不持久化
  }
  emit();
}

/** 当前隐藏的菜单 key 列表（引用稳定，可直接进 useSyncExternalStore） */
export function useHiddenNavKeys(): string[] {
  return useSyncExternalStore(
    subscribe,
    () => cache,
    () => cache,
  );
}

export function useNavPrefs(): {
  hidden: string[];
  isHidden: (key: string) => boolean;
  toggle: (key: string) => void;
  reset: () => void;
} {
  const hidden = useHiddenNavKeys();
  const toggle = useCallback((key: string) => {
    const item = NAV_ITEMS.find((i) => i.key === key);
    if (!item || item.locked) return;
    setHidden(cache.includes(key) ? cache.filter((k) => k !== key) : [...cache, key]);
  }, []);
  const reset = useCallback(() => {
    setHidden([]);
  }, []);
  return { hidden, isHidden: (key) => hidden.includes(key), toggle, reset };
}

/** 侧栏实际渲染的菜单（已过滤隐藏项） */
export function useVisibleNavItems(): NavItem[] {
  const hidden = useHiddenNavKeys();
  return NAV_ITEMS.filter((i) => i.locked === true || !hidden.includes(i.key));
}
